import { i18n } from '@lingui/core';
import { Trans } from '@lingui/react';
import { Button, TextField } from '@warp-ds/react';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

type Props = {
  onSubmit: (data: CarInfoFormInputs) => void;
  error?: string;
  placeholder: string;
  defaultValue?: string;
};

export type CarInfoFormInputs = {
  regnr: string;
};

export const RegistrationNumberForm = ({ onSubmit, error, placeholder, defaultValue }: Props) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    reset,
  } = useForm<CarInfoFormInputs>({
    mode: 'onSubmit',
    defaultValues: { regnr: defaultValue || '' },
  });

  // Track previous defaultValue to only reset when it actually changes
  const prevDefaultValueRef = useRef(defaultValue);

  useEffect(() => {
    if (error) {
      setError('regnr', { message: error });
    } else {
      clearErrors('regnr');
    }
  }, [error, setError, clearErrors]);

  // Reset form when defaultValue changes (e.g., when navigating with a new registration number in URL)
  useEffect(() => {
    if (defaultValue !== prevDefaultValueRef.current) {
      prevDefaultValueRef.current = defaultValue;
      if (defaultValue !== undefined) {
        reset({ regnr: defaultValue });
      }
    }
  }, [defaultValue, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <label htmlFor="regnr" className="antialiased block relative text-s font-bold pb-4 cursor-pointer s-text">
        <Trans
          id="mobility-item-insertion.registration-number-page.input.label"
          comment="Label for the input field where users enter the car's registration number"
          message="Registration number"
        />
      </label>
      <div className="flex gap-16 mb-32">
        <TextField
          className="flex-1 md:flex-initial md:w-2/10 children:children:uppercase!"
          id="regnr"
          placeholder={placeholder}
          disabled={isSubmitting}
          {...register('regnr', {
            setValueAs: (value) => String(value).replace(' ', '').toUpperCase(),
            required: true,
            minLength: {
              value: 2,
              message: i18n.t({
                id: 'vehicle-lookup-podlet.validation.licensePlateTooShort',
                message: 'Must be at least 2 characters',
                comment: 'Validation message indicating that the input is too short',
              }),
            },
            pattern: {
              value: /^[A-ZÆØÅÄÖ0-9-]{2,8}$/i,
              message: i18n.t({
                id: 'vehicle-lookup-podlet.validation.invalidLicensePlate',
                message: 'Not a valid registration number',
                comment: 'Validation message indicating that the input is not a valid vehicle registration number',
              }),
            },
          })}
          invalid={!!errors.regnr?.message}
          helpText={errors.regnr?.message}
        />
        <Button primary type="submit" style={{ height: 'fit-content' }} loading={isSubmitting}>
          <Trans
            id="mobility-item-insertion.registration-number-page.input.button"
            comment="Button text for searching car information using the registration number"
            message="Search"
          />
        </Button>
      </div>
    </form>
  );
};
